import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Switch,
  InputNumber,
  DatePicker,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GiftOutlined,
  PercentageOutlined,
  DollarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const Coupons = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form] = Form.useForm();
  const discountType = Form.useWatch('discount_type', form);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  // Fetch coupons with filters
  const { data: coupons = [], isLoading } = useQuery(
    ['coupons', searchTerm, statusFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);

        const response = await api.get(`/admin/coupons?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch coupons:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Client-side filtering fallback
  const filteredCoupons = useMemo(() => {
    if (!Array.isArray(coupons)) return [];
    let filtered = [...coupons];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.code && c.code.toLowerCase().includes(searchLower)) ||
          (c.discount_type && c.discount_type.toLowerCase().includes(searchLower)),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    return filtered;
  }, [coupons, searchTerm, statusFilter]);

  // Create coupon mutation
  const createCouponMutation = useMutation(
    async (couponData) => {
      const response = await api.post('/admin/coupons', couponData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Coupon created successfully');
        setIsModalVisible(false);
        form.resetFields();
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create coupon');
      },
    },
  );

  // Update coupon mutation
  const updateCouponMutation = useMutation(
    async ({ id, ...couponData }) => {
      const response = await api.put(`/admin/coupons/${id}`, couponData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Coupon updated successfully');
        setIsModalVisible(false);
        setEditingCoupon(null);
        form.resetFields();
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update coupon');
      },
    },
  );

  // Delete coupon mutation
  const deleteCouponMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/coupons/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Coupon deleted successfully');
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete coupon');
      },
    },
  );

  // Toggle coupon status mutation
  const toggleStatusMutation = useMutation(
    async ({ id, status }) => {
      await api.patch(`/admin/coupons/${id}/toggle-status`, { status });
    },
    {
      onSuccess: () => {
        message.success('Coupon status updated successfully');
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update coupon status');
      },
    },
  );

  const handleSubmit = async (values) => {
    try {
      if (values.validFrom) {
        values.valid_from = values.validFrom.format('YYYY-MM-DD');
        delete values.validFrom;
      }
      if (values.validTo) {
        values.valid_until = values.validTo.format('YYYY-MM-DD');
        delete values.validTo;
      }
      if (values.minimum_order_amount !== undefined) {
        values.min_order_amount = values.minimum_order_amount;
        delete values.minimum_order_amount;
      }

      if (editingCoupon) {
        updateCouponMutation.mutate({ id: editingCoupon.id, ...values });
      } else {
        createCouponMutation.mutate(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      ...coupon,
      validFrom: coupon.valid_from ? dayjs(coupon.valid_from) : null,
      validTo: coupon.valid_until ? dayjs(coupon.valid_until) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteCouponMutation.mutate(id);
  };

  const handleToggleStatus = (coupon) => {
    const newStatus = coupon.status === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate({ id: coupon.id, status: newStatus });
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
      render: (code) => (
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
          {code}
        </Tag>
      ),
    },
    {
      title: 'Discount Type',
      dataIndex: 'discount_type',
      key: 'discount_type',
      sorter: (a, b) => (a.discount_type || '').localeCompare(b.discount_type || ''),
      render: (type) => (
        <Tag
          color={type === 'percentage' ? 'green' : 'orange'}
          icon={type === 'percentage' ? <PercentageOutlined /> : <DollarOutlined />}
        >
          {type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
        </Tag>
      ),
    },
    {
      title: 'Discount Value',
      dataIndex: 'discount_value',
      key: 'discount_value',
      sorter: (a, b) => (parseFloat(a.discount_value) || 0) - (parseFloat(b.discount_value) || 0),
      render: (value, record) => (
        <span>{record.discount_type === 'percentage' ? `${value}%` : `₨${value}`}</span>
      ),
    },
    {
      title: 'Minimum Order',
      dataIndex: 'minimum_order_amount',
      key: 'minimum_order_amount',
      render: (amount) => (amount ? `₨${amount}` : 'No minimum'),
    },
    {
      title: 'Usage Limit',
      dataIndex: 'usage_limit',
      key: 'usage_limit',
      render: (limit, record) => (
        <span>
          {record.used_count || 0} / {limit || 'Unlimited'}
        </span>
      ),
    },
    {
      title: 'Valid Period',
      key: 'valid_period',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            From:{' '}
            {record.valid_from ? dayjs(record.valid_from).format('MMM DD, YYYY') : 'No start date'}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            To: {record.valid_to ? dayjs(record.valid_to).format('MMM DD, YYYY') : 'No expiry'}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Switch
              size="small"
              checked={record.status === 'active'}
              onChange={() => handleToggleStatus(record)}
              loading={toggleStatusMutation.isLoading}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this coupon?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deleteCouponMutation.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const activeCoupons = Array.isArray(coupons)
    ? coupons.filter((c) => c.status === 'active').length
    : 0;
  const totalDiscount = Array.isArray(coupons)
    ? coupons.reduce((sum, coupon) => {
        return (
          sum +
          (coupon.used_count || 0) *
            (coupon.discount_type === 'percentage'
              ? (coupon.discount_value / 100) * (coupon.minimum_order_amount || 100)
              : coupon.discount_value)
        );
      }, 0)
    : 0;

  return (
    <div>
      <Title level={2}>
        <GiftOutlined /> Coupons Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Coupons"
              value={coupons.length}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Coupons"
              value={activeCoupons}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Usage"
              value={coupons.reduce((sum, c) => sum + (c.used_count || 0), 0)}
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Discount Given"
              value={totalDiscount}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters and Actions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search coupons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCoupon(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
            >
              Add New Coupon
            </Button>
          </Col>
        </Row>

        {/* Coupons Table */}
        <Table
          columns={columns}
          dataSource={filteredCoupons}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: filteredCoupons.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} coupons`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Add/Edit Coupon Modal */}
      <Modal
        title={editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCoupon(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="code"
                label="Coupon Code"
                rules={[
                  { required: true, message: 'Please enter coupon code' },
                  {
                    pattern: /^[A-Z0-9_]+$/,
                    message: 'Only uppercase letters, numbers, and underscores allowed',
                  },
                ]}
              >
                <Input placeholder="e.g., WELCOME50" style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="discount_type"
                label="Discount Type"
                rules={[{ required: true, message: 'Please select discount type' }]}
              >
                <Select placeholder="Select discount type">
                  <Option value="percentage">Percentage</Option>
                  <Option value="fixed">Fixed Amount</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="discount_value"
                label="Discount Value"
                rules={[
                  { required: true, message: 'Please enter discount value' },
                  { type: 'number', min: 0, message: 'Discount value must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter discount value"
                  min={0}
                  max={100}
                  formatter={(value) => {
                    return discountType === 'percentage' ? `${value}%` : `₨${value}`;
                  }}
                  parser={(value) => value.replace(/[%₨\s,]/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="minimum_order_amount" label="Minimum Order Amount">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter minimum order amount"
                  min={0}
                  formatter={(value) => `₨${value}`}
                  parser={(value) => value.replace(/₨\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="usage_limit" label="Usage Limit">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Leave empty for unlimited"
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="validFrom" label="Valid From">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="validTo" label="Valid To">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingCoupon(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createCouponMutation.isLoading || updateCouponMutation.isLoading}
              >
                {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Coupons;
